import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users, Filter, ArrowRight, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Badge, TelegramStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SearchInput } from '../../components/ui/Input.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchUsers } from '../../api/users.js';
import { formatDate, formatRelativeTime } from '../../lib/utils.js';

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [telegramFilter, setTelegramFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { t } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users', { cursor, status: statusFilter }],
    queryFn: () => fetchUsers({ limit: 15, cursor, status: statusFilter !== 'all' ? statusFilter : undefined }),
  });

  const filteredUsers = (data?.items || []).filter((u) => {
    // 1. Telegram filter
    if (telegramFilter === 'linked' && !u.telegram_chat_id) return false;
    if (telegramFilter === 'unlinked' && u.telegram_chat_id) return false;

    // 2. Search query
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      u.id.toLowerCase().includes(term) ||
      (u.phone_number && u.phone_number.toLowerCase().includes(term)) ||
      (u.telegram_chat_id && u.telegram_chat_id.toLowerCase().includes(term)) ||
      (u.country && u.country.toLowerCase().includes(term))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('users.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('users.description')}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="w-full sm:w-80">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('users.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Telegram Filter */}
            <div className="flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-[#229ED9]" />
              <select
                value={telegramFilter}
                onChange={(e) => setTelegramFilter(e.target.value as any)}
                className="text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">{t('users.allTelegram')}</option>
                <option value="linked">{t('users.linkedOnly')}</option>
                <option value="unlinked">{t('users.unlinkedOnly')}</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">{t('users.allStatuses')}</option>
                <option value="active">{t('users.activeStatus')}</option>
                <option value="suspended">{t('users.suspendedStatus')}</option>
                <option value="deleted">{t('users.deletedStatus')}</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={6} cols={6} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('users.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('users.emptyTitle')}
                description={t('users.emptyDesc')}
                actionText={t('common.resetFilters')}
                onAction={() => {
                  setSearch('');
                  setStatusFilter('all');
                  setTelegramFilter('all');
                }}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('users.tableUserId')}</th>
                  <th className="py-3 px-4">{t('users.tablePhone')}</th>
                  <th className="py-3 px-4">{t('users.tableTelegram')}</th>
                  <th className="py-3 px-4">{t('users.tableLastLogin')}</th>
                  <th className="py-3 px-4">{t('users.tableStatus')}</th>
                  <th className="py-3 px-4">{t('users.tableRegistered')}</th>
                  <th className="py-3 px-4 text-right">{t('users.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer transition-colors"
                  >
                    {/* User ID */}
                    <td className="py-3 px-4">
                      <CopyButton value={user.id} truncate={true} className="font-mono text-indigo-600 dark:text-indigo-400 font-medium" />
                    </td>

                    {/* Telefon Raqami */}
                    <td className="py-3 px-4">
                      {user.phone_number ? (
                        <span className="font-mono font-medium text-slate-800 dark:text-slate-200">
                          {user.phone_number}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">{t('users.noPhone')}</span>
                      )}
                    </td>

                    {/* Telegram Holati */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <TelegramStatusBadge chatId={user.telegram_chat_id} />
                        {user.telegram_chat_id && (
                          <CopyButton value={user.telegram_chat_id} truncate={false} className="p-0.5" />
                        )}
                      </div>
                    </td>

                    {/* So'nggi kirish/tasdiqlash vaqti */}
                    <td className="py-3 px-4">
                      {user.last_login_at || user.updated_at ? (
                        <div>
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {formatRelativeTime(user.last_login_at || user.updated_at)}
                          </span>
                          <span className="block text-[10px] text-slate-400">
                            {formatDate(user.last_login_at || user.updated_at)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">{t('users.noLoginRecorded')}</span>
                      )}
                    </td>

                    {/* Holati */}
                    <td className="py-3 px-4">
                      <Badge variant={user.status === 'active' ? 'success' : user.status === 'suspended' ? 'danger' : 'neutral'}>
                        {user.status}
                      </Badge>
                    </td>

                    {/* Ro'yxatdan o'tgan */}
                    <td className="py-3 px-4 text-slate-500">
                      {formatDate(user.created_at)}
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/users/${user.id}`);
                        }}
                      >
                        {t('common.details')} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>

        {/* Cursor Pagination Controls */}
        {data && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500">
            <span>{t('users.totalCount', { count: filteredUsers.length })}</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!cursor}
                onClick={() => setCursor(undefined)}
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t('common.firstPage')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data.has_more || !data.next_cursor}
                onClick={() => setCursor(data.next_cursor || undefined)}
              >
                {t('common.nextPage')} <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
